import { getCurrencyRates } from './site-settings-integration';

/**
 * Maps locale codes to their corresponding currency codes
 */
export const localeToCurrency: Record<string, string> = {
  en: 'GBP',   // English - US Dollar
  fr: 'EUR',   // French - Euro
  es: 'EUR',   // Spanish - Euro
  de: 'EUR',   // German - Euro
  it: 'EUR',   // Italian - Euro
  nl: 'EUR',   // Dutch - Euro
  se: 'SEK',   // Swedish - Swedish Krona
  pl: 'PLN',   // Polish - Polish Zloty
};

/**
 * Default base currency for the application
 */
export const BASE_CURRENCY = 'EUR';

/**
 * Gets the currency code for a given locale
 */
export function getCurrencyFromLocale(locale: string): string {
  return localeToCurrency[locale] || BASE_CURRENCY;
}

/**
 * Converts price to target currency and returns both the converted amount and conversion details
 */
export function convertPriceWithRate(
  basePrice: number, 
  targetCurrency: string,
  debug = false
): {
  convertedAmount: number;
  rate: number;
  baseCurrency: string;
  baseAmount: number;
} {
  // Get the latest rates with debug mode if requested
  const rates = getCurrencyRates(debug);
  const rate = rates[targetCurrency] || 1;
  const convertedAmount = basePrice * rate;
  
  return {
    convertedAmount,
    rate,
    baseCurrency: BASE_CURRENCY,
    baseAmount: basePrice
  };
}

/**
 * Formats a price according to the specified locale and currency
 */
export function formatCurrency(
  amount: number,
  locale: string,
  currency: string
): string {
  // Map custom locale codes to valid Intl locales (e.g. 'se' → 'sv-SE')
  const nfLocale = locale === 'se' ? 'sv-SE' : locale;
  return new Intl.NumberFormat(nfLocale, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Returns formatted price from stored order data
 * Uses the persisted currency and amount from the order
 */
export function formatOrderPrice(
  order: { 
    currency: string; 
    total_amount: number;
    currency_rate?: number;
    base_amount?: number;
  },
  locale: string
): string {
  return formatCurrency(order.total_amount, locale, order.currency);
}

/**
 * Server-side: Converts and formats a price for use in metadata based on locale
 * Handles both the numerical conversion and proper formatting for SEO
 */
export async function getMetadataPrice(
  basePrice: number, 
  locale: string,
  debug = false
): Promise<{
  convertedAmount: number;
  formattedValue: string;
}> {
  // Force-refresh the cache to ensure we have the most up-to-date rates
  const { getCurrencyRates, clearSettingsCache } = await import('./site-settings-integration');
  
  // Optionally clear the cache to force a refresh from the database
  if (debug) {
    await clearSettingsCache();
  }
  
  // Get the latest rates with debug enabled
  const rates = getCurrencyRates(debug);
  const targetCurrency = getCurrencyFromLocale(locale);
  const rate = rates[targetCurrency] || 1;
  const convertedAmount = basePrice * rate;
  
  // Format without currency code for metadata (more natural in descriptions)
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: targetCurrency,
    currencyDisplay: 'symbol'
  });
  
  const formattedValue = formatter.format(convertedAmount);
  
  return {
    convertedAmount,
    formattedValue
  };
} 